import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  APPROXIMATE_APPROVAL_RATE,
  DEFAULT_REDO_KEEP_PROPORTION,
} from "@redotech/redo-model/shipping-claims/shipping-claim";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import * as styles from "./auto-claims-upsell-modal.module.css";

export const AutoClaimsUpsellModal = memo(function AutoClaimsUpsellModal({
  open,
  onClose,
  onAccept,
}: {
  open: boolean;
  onClose: () => void;
  onAccept?: () => void;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [loading, setLoading] = useState(false);

  const statsLoad = useLoad(
    async (signal) => {
      if (!open) {
        return undefined;
      }
      return await client.getProactiveClaimsStats({}, { signal });
    },
    [open],
  );

  const handleAccept = useHandler(async () => {
    setLoading(true);
    try {
      await client.acceptAutoClaimsFiling({});
      onAccept?.();
      onClose();
    } finally {
      setLoading(false);
    }
  });

  if (!open) {
    return null;
  }

  const stats = statsLoad?.value;
  const hasCompellingStats =
    stats !== undefined &&
    stats.proactiveClaimCount >= 2 &&
    stats.estimatedMerchantSavings >= 50;

  return (
    <RedoBaseModal
      isOpen={open}
      onModalCloseRequested={onClose}
      otherClasses={[styles.modal]}
    >
      <Flex dir="column" gap="none" w="full">
        <Flex dir="column" gap="md" p="3xl">
          {hasCompellingStats ? (
            <>
              <Text fontSize="lg" fontWeight="semibold" textColor="primary">
                You left{" "}
                <Text as="span" fontWeight="semibold" textColor="success">
                  ${stats.estimatedMerchantSavings.toFixed(2)}
                </Text>{" "}
                on the table in the last 30 days
              </Text>
              <Text fontSize="sm" textColor="tertiary">
                Never file a carrier claim again, let Redo fight your claims for
                you
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="lg" fontWeight="semibold" textColor="primary">
                You're leaving money on the table
              </Text>
              <Text fontSize="sm" textColor="tertiary">
                Never file a carrier claim again, let Redo fight your claims for
                you
              </Text>
            </>
          )}
        </Flex>
        <Flex
          bgColor="tertiary"
          borderColor="secondary"
          borderWidth="1px"
          dir="column"
          gap="3xl"
          p="3xl"
          w="full"
        >
          {hasCompellingStats && (
            <Flex dir="row" justify="space-between" w="full">
              <Flex dir="column" gap="none" pr="lg">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Claims missed
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {stats.proactiveClaimCount}
                </Text>
                <Text fontSize="xs" textColor="tertiary">
                  last 30 days
                </Text>
              </Flex>
              <Flex dir="column" gap="none" pr="lg">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Est. recovery
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  ${stats.estimatedMerchantSavings.toFixed(2)}
                </Text>
                <Text fontSize="xs" textColor="tertiary">
                  uncollected
                </Text>
              </Flex>
              <Flex dir="column" gap="none">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Win rate
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {(APPROXIMATE_APPROVAL_RATE * 100).toFixed(0)}%
                </Text>
                <Text fontSize="xs" textColor="tertiary">
                  Redo average
                </Text>
              </Flex>
            </Flex>
          )}
          <Flex dir="column" gap="xs">
            <Flex dir="row" gap="md">
              <Text fontSize="sm" fontWeight="semibold">
                •
              </Text>
              <Text fontSize="sm">
                Redo files claims automatically for lost or late shipments,{" "}
                <Text as="span" fontWeight="semibold">
                  no work on your end.
                </Text>
              </Text>
            </Flex>
            <Flex dir="row" gap="md">
              <Text fontSize="sm" fontWeight="semibold">
                •
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="semibold">
                  Free to turn on
                </Text>
                , we only earn when you do.
              </Text>
            </Flex>
            <Flex dir="row" gap="md">
              <Text fontSize="sm" fontWeight="semibold">
                •
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="semibold">
                  You keep{" "}
                  {((1 - DEFAULT_REDO_KEEP_PROPORTION) * 100).toFixed(0)}%
                </Text>{" "}
                of every approved claim.
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex dir="row" gap="md" justify="flex-end" p="3xl">
          <RedoButton
            disabled={loading}
            hierarchy="secondary"
            onClick={onClose}
            text="No thanks"
          />
          <RedoButton
            hierarchy="primary"
            onClick={handleAccept}
            pending={loading}
            text="Start recovering claims"
            theme="success"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
